import { AppContext, IAppContext } from 'appState/appContext';
import { useFetchData } from 'hooks/useFetchData';
import { getTeaserInfo } from 'backend/teasers';
import React, { useCallback, useContext, useEffect, useReducer } from 'react';
import {
	EditTeaserContext,
	editTeaserInitialState,
	IEditTeaserTotalSchedule,
	TEditTeaserControlPoint,
	TEditTeaserSelectedContentType,
	TEditTeaserTransitionType,
	TEditTeaserVisibleForm,
	TSourceUrlLocally,
	TTypeOfEditTeaserModalWindow,
} from './editTeaserContext';
import { editTeaserReducer } from './editTeaserReducer';
import { EditTeaserActionTypes } from './editTeaserActions';
import { IItemForGetShortInfo, TFileId, IEditTeaser, IGetTeaserInfo } from 'types';

const EditTeaserState = ({ children }: JSX.ElementChildrenAttribute) => {
	const [state, dispatch] = useReducer(editTeaserReducer, editTeaserInitialState);
	const { showLoader, hideLoader } = useContext<IAppContext>(AppContext);

	const params = new URL(document.location.href).searchParams;
	const teaserID = params.get('teaserID');

	const { response, loading, refetch } = useFetchData<IGetTeaserInfo, string>(
		teaserID ? getTeaserInfo : null,
		teaserID || '',
	);

	useEffect(() => {
		if (state.needRefetchData) {
			refetch();
			dispatch({
				type: EditTeaserActionTypes.REFETCH_DATA,
				payload: {
					needRefetch: false,
				},
			});
		}
	}, [state.needRefetchData]);

	useEffect(() => {
		loading ? showLoader() : hideLoader();
	}, [loading]);

	useEffect(() => {
		if (response) {
			const getTransitionType = (): TEditTeaserTransitionType => {
				const teaserData = response.data.teaser;
				if (teaserData.dishTransition) {
					return 'dish';
				}
				if (teaserData.gameLaunchTransition) {
					return 'game';
				}
				return null;
			};

			dispatch({
				type: EditTeaserActionTypes.SET_ALL_DATA,
				payload: {
					teaserID: response.data.teaser.id,
					filePath: response.data.teaser.fileId || '',
					selectedCustomer: response.data.teaser.customerId,
					selectedRestaurants: response.data.teaser.restaurants.map((el) => el.id),
					selectedContentType: response.data.teaser.contentType,
					description: response.data.teaser.description || '',
					schedule: response.data.teaser.schedule?.[0] || '', // first schedule in array goes to main schedule input, other ones - to additional schedule inputs
					totalSchedules:
						response.data.teaser.schedule?.slice(1).map((el, index) => ({
							id: String(index + 1),
							value: el,
						})) || [],
					selectedTransitionType: getTransitionType(),
					dishTransition: response.data.teaser.dishTransition,
					selectedTransitionDishCategory: response.data.teaser.dishTransition?.category.id || null,
					selectedTransitionDishSubCategory: response.data.teaser.dishTransition?.subCategory?.id || null,
					selectedTransitionDishName: response.data.teaser.dishTransition?.dish?.id || null,
					selectedTransitionGameCategory: response.data.teaser.gameLaunchTransition?.gameCategory?.id || null,
					selectedTransitionGameName: response.data.teaser.gameLaunchTransition?.game?.id || null,
					selectedControlPoint: response.data.teaser.controlPoint,
					delayTime: response.data.teaser.delayTime ? String(response.data.teaser.delayTime) : '',
					launchCount: response.data.teaser.launchCount ? String(response.data.teaser.launchCount) : '',
					countDown: response.data.teaser.countdown ? String(response.data.teaser.countdown) : '',
				},
			});
			dispatch({
				type: EditTeaserActionTypes.SET_INITIAL_FORM_DATA,
				payload: {
					initialFormData: {
						filePath: response.data.teaser.fileId || '',
						selectedCustomer: response.data.teaser.customerId,
						selectedRestaurants: response.data.teaser.restaurants.map((el) => el.id),
						selectedContentType: response.data.teaser.contentType,
						description: response.data.teaser.description || '',
						schedule: response.data.teaser.schedule?.[0] || '',
						totalSchedules:
							response.data.teaser.schedule?.slice(1).map((el, index) => ({
								id: String(index + 1),
								value: el,
							})) || [],
						selectedTransitionType: getTransitionType(),
						selectedTransitionDishCategory: response.data.teaser.dishTransition?.category.id || null,
						selectedTransitionDishSubCategory: response.data.teaser.dishTransition?.subCategory?.id || null,
						selectedTransitionDishName: response.data.teaser.dishTransition?.dish?.id || null,
						selectedTransitionGameCategory:
							response.data.teaser.gameLaunchTransition?.gameCategory?.id || null,
						selectedTransitionGameName: response.data.teaser.gameLaunchTransition?.game?.id || null,
						selectedControlPoint: response.data.teaser.controlPoint,
						delayTime: response.data.teaser.delayTime ? String(response.data.teaser.delayTime) : '',
						launchCount: response.data.teaser.launchCount ? String(response.data.teaser.launchCount) : '',
						countDown: response.data.teaser.countdown ? String(response.data.teaser.countdown) : '',
					},
				},
			});
		}
	}, [response]);

	const refetchData = useCallback((value: boolean) => {
		dispatch({
			type: EditTeaserActionTypes.REFETCH_DATA,
			payload: {
				needRefetch: value,
			},
		});
	}, []);

	const changeVisibleForm = useCallback((value: TEditTeaserVisibleForm) => {
		dispatch({
			type: EditTeaserActionTypes.CHANGE_VISIBLE_FORM,
			payload: {
				visibleForm: value,
			},
		});
	}, []);

	const setFile = useCallback((fileId: TFileId, sourceUrlLocally: TSourceUrlLocally) => {
		dispatch({
			type: EditTeaserActionTypes.SET_FILE,
			payload: {
				fileId: fileId,
				sourceUrlLocally: sourceUrlLocally,
			},
		});
	}, []);

	const deleteVideo = useCallback(() => dispatch({ type: EditTeaserActionTypes.DELETE_VIDEO }), []);

	const setFetchedCustomers = useCallback((value: IItemForGetShortInfo[]) => {
		dispatch({
			type: EditTeaserActionTypes.SET_FETCHED_CUSTOMERS,
			payload: {
				customers: value,
			},
		});
	}, []);

	const setCustomer = useCallback((value: Nullable<string>) => {
		dispatch({
			type: EditTeaserActionTypes.SET_CUSTOMER,
			payload: {
				customer: value,
			},
		});
	}, []);

	const setFetchedRestaurants = useCallback((value: IItemForGetShortInfo[]) => {
		dispatch({
			type: EditTeaserActionTypes.SET_FETCHED_RESTAURANTS,
			payload: {
				restaurants: value,
			},
		});
	}, []);

	const setRestaurants = useCallback((value: string[]) => {
		dispatch({
			type: EditTeaserActionTypes.SET_RESTAURANTS,
			payload: {
				restaurants: value,
			},
		});
	}, []);

	const setContentType = useCallback((value: TEditTeaserSelectedContentType) => {
		dispatch({
			type: EditTeaserActionTypes.SET_CONTENT_TYPE,
			payload: {
				contentType: value,
			},
		});
	}, []);

	const changeDescription = useCallback((value: string) => {
		dispatch({
			type: EditTeaserActionTypes.CHANGE_DESCRIPTION,
			payload: {
				description: value,
			},
		});
	}, []);

	const changeSchedule = useCallback((value: string) => {
		dispatch({
			type: EditTeaserActionTypes.CHANGE_SCHEDULE,
			payload: {
				schedule: value,
			},
		});
	}, []);

	const changeAdditionalSchedule = useCallback(
		(fieldId: string, value: string) => {
			const updatedSchedules = state.totalSchedules.map((el) => (el.id === fieldId ? { ...el, value } : el));

			dispatch({
				type: EditTeaserActionTypes.CHANGE_ADDITIONAL_SCHEDULE,
				payload: {
					updatedSchedules,
				},
			});
		},
		[state.totalSchedules],
	);

	const setSchedules = useCallback((value: IEditTeaserTotalSchedule[]) => {
		dispatch({
			type: EditTeaserActionTypes.SET_SCHEDULES,
			payload: {
				updatedSchedules: value,
			},
		});
	}, []);

	const setTransitionType = useCallback(
		(value: TEditTeaserTransitionType) => {
			dispatch({
				type: EditTeaserActionTypes.SET_TRANSITION_TYPE,
				payload: {
					transitionType: state.selectedTransitionType === value ? null : value,
				},
			});
		},
		[state.selectedTransitionType],
	);

	const setFetchedDishCategories = useCallback((value: IItemForGetShortInfo[]) => {
		dispatch({
			type: EditTeaserActionTypes.SET_FETCHED_DISH_CATEGORIES,
			payload: {
				dishCategories: value,
			},
		});
	}, []);

	const setTransitionDishCategory = useCallback((value: Nullable<string>) => {
		dispatch({
			type: EditTeaserActionTypes.SET_TRANSITION_DISH_CATEGORY,
			payload: {
				transitionDishCategory: value,
			},
		});
	}, []);

	const setFetchedDishSubCategories = useCallback((value: IItemForGetShortInfo[]) => {
		dispatch({
			type: EditTeaserActionTypes.SET_FETCHED_DISH_SUB_CATEGORIES,
			payload: {
				dishSubCategories: value,
			},
		});
	}, []);

	const setTransitionDishSubCategory = useCallback((value: Nullable<string>) => {
		dispatch({
			type: EditTeaserActionTypes.SET_TRANSITION_DISH_SUB_CATEGORY,
			payload: {
				transitionDishSubCategory: value,
			},
		});
	}, []);

	const setFetchedDishes = useCallback((value: IItemForGetShortInfo[]) => {
		dispatch({
			type: EditTeaserActionTypes.SET_FETCHED_DISHES,
			payload: {
				dishes: value,
			},
		});
	}, []);

	const setTransitionDishName = useCallback((value: Nullable<string>) => {
		dispatch({
			type: EditTeaserActionTypes.SET_TRANSITION_DISH_NAME,
			payload: {
				transitionDishName: value,
			},
		});
	}, []);

	const setFetchedGameCategories = useCallback((value: IItemForGetShortInfo[]) => {
		dispatch({
			type: EditTeaserActionTypes.SET_FETCHED_GAME_CATEGORIES,
			payload: {
				gameCategories: value,
			},
		});
	}, []);

	const setTransitionGameCategory = useCallback((value: Nullable<string>) => {
		dispatch({
			type: EditTeaserActionTypes.SET_TRANSITION_GAME_CATEGORY,
			payload: {
				transitionGameCategory: value,
			},
		});
	}, []);

	const setFetchedGames = useCallback((value: IItemForGetShortInfo[]) => {
		dispatch({
			type: EditTeaserActionTypes.SET_FETCHED_GAMES,
			payload: {
				games: value,
			},
		});
	}, []);

	const setTransitionGameName = useCallback((value: Nullable<string>) => {
		dispatch({
			type: EditTeaserActionTypes.SET_TRANSITION_GAME_NAME,
			payload: {
				transitionGameName: value,
			},
		});
	}, []);

	const setControlPoint = useCallback((value: TEditTeaserControlPoint) => {
		dispatch({
			type: EditTeaserActionTypes.SET_CONTROL_POINT,
			payload: {
				controlPoint: value,
			},
		});
	}, []);

	const changeDelayTime = useCallback((value: string) => {
		dispatch({
			type: EditTeaserActionTypes.CHANGE_DELAY_TIME,
			payload: {
				delayTime: value,
			},
		});
	}, []);

	const changeLaunchCount = useCallback((value: string) => {
		dispatch({
			type: EditTeaserActionTypes.CHANGE_LAUNCH_COUNT,
			payload: {
				launchCount: value,
			},
		});
	}, []);

	const changeCountDown = useCallback((value: string) => {
		dispatch({
			type: EditTeaserActionTypes.CHANGE_COUNTDOWN,
			payload: {
				countDown: value,
			},
		});
	}, []);

	const setDataForSubmit = useCallback((value: IEditTeaser | null) => {
		dispatch({
			type: EditTeaserActionTypes.SET_DATA_FOR_SUBMIT,
			payload: {
				dataForSubmit: value,
			},
		});
	}, []);

	const changeTypeOfModalWindow = useCallback((value: TTypeOfEditTeaserModalWindow) => {
		dispatch({
			type: EditTeaserActionTypes.CHANGE_TYPE_OF_MODAL_WINDOW,
			payload: {
				typeOfModalWindow: value,
			},
		});
	}, []);

	const resetFields = useCallback(() => {
		dispatch({
			type: EditTeaserActionTypes.RESET_FIELDS,
		});
	}, []);

	return (
		<EditTeaserContext.Provider
			value={{
				refetchData,
				changeVisibleForm,
				setFile,
				deleteVideo,
				setFetchedCustomers,
				setCustomer,
				setFetchedRestaurants,
				setRestaurants,
				setContentType,
				changeDescription,
				changeSchedule,
				changeAdditionalSchedule,
				setSchedules,
				setTransitionType,
				setFetchedDishCategories,
				setTransitionDishCategory,
				setFetchedDishSubCategories,
				setTransitionDishSubCategory,
				setFetchedDishes,
				setTransitionDishName,
				setFetchedGameCategories,
				setTransitionGameCategory,
				setFetchedGames,
				setTransitionGameName,
				setControlPoint,
				changeDelayTime,
				changeLaunchCount,
				changeCountDown,
				setDataForSubmit,
				changeTypeOfModalWindow,
				resetFields,
				needRefetchData: state.needRefetchData,
				initialFormData: state.initialFormData,
				visibleForm: state.visibleForm,
				teaserID: state.teaserID,
				fileId: state.fileId,
				filePath: state.filePath,
				sourceUrlLocally: state.sourceUrlLocally,
				fetchedCustomers: state.fetchedCustomers,
				selectedCustomer: state.selectedCustomer,
				fetchedRestaurants: state.fetchedRestaurants,
				selectedRestaurants: state.selectedRestaurants,
				selectedContentType: state.selectedContentType,
				description: state.description,
				schedule: state.schedule,
				totalSchedules: state.totalSchedules,
				selectedTransitionType: state.selectedTransitionType,
				dishTransition: state.dishTransition,
				fetchedDishCategories: state.fetchedDishCategories,
				selectedTransitionDishCategory: state.selectedTransitionDishCategory,
				fetchedDishSubCategories: state.fetchedDishSubCategories,
				selectedTransitionDishSubCategory: state.selectedTransitionDishSubCategory,
				fetchedDishes: state.fetchedDishes,
				selectedTransitionDishName: state.selectedTransitionDishName,
				fetchedGameCategories: state.fetchedGameCategories,
				selectedTransitionGameCategory: state.selectedTransitionGameCategory,
				fetchedGames: state.fetchedGames,
				selectedTransitionGameName: state.selectedTransitionGameName,
				selectedControlPoint: state.selectedControlPoint,
				delayTime: state.delayTime,
				launchCount: state.launchCount,
				countDown: state.countDown,
				dataForSubmit: state.dataForSubmit,
				typeOfModalWindow: state.typeOfModalWindow,
			}}>
			{children}
		</EditTeaserContext.Provider>
	);
};

export default EditTeaserState;
